// Decoder conditioning and the view tensor: cond is [gaze(3), head rotation 6D, head t(3)].

import { matrix3, vector3 } from '../math/index.ts'
import type { Vector3 } from '../math/index.ts'
import type { HeadPose } from './eyeChart.ts'
import { EYE_SIDES } from './eyeSide.ts'
import type { EyeSide } from './eyeSide.ts'

export type CondOwners = {
  owners: Record<EyeSide, EyeSide>
  alignment: Record<EyeSide, number>
}

/** Which eye's live gaze belongs in each chart's cond slot, DERIVED from the charts' own
 *  reference gaze: Myra's pairing is crossed, so the names cannot be trusted. */
export function resolveCondOwners(opticalAxes: Record<EyeSide, Vector3>,
                                  headPose: HeadPose,
                                  gazeReference: Record<EyeSide, Vector3 | null>): CondOwners {
  const owners = {} as Record<EyeSide, EyeSide>
  const alignment = {} as Record<EyeSide, number>
  for (const side of EYE_SIDES) {
    const reference = gazeReference[side]
    if (!reference) {
      owners[side] = side
      alignment[side] = NaN
      continue
    }
    const world = matrix3.transform(headPose.rotation, reference)
    let best = side
    let bestAlignment = -Infinity
    for (const candidate of EYE_SIDES) {
      const score = vector3.dot(world, opticalAxes[candidate])
      if (score <= bestAlignment) continue
      bestAlignment = score
      best = candidate
    }
    owners[side] = best
    alignment[side] = bestAlignment
  }
  return { owners, alignment }
}

/** A world gaze direction in the head frame the cond slot is expressed in. */
export function gazeToHeadSpace(worldDirection: Vector3, headPose: HeadPose): Vector3 {
  return vector3.normalize(matrix3.transformTransposed(headPose.rotation, worldDirection))
}

// The 6D rotation part is the first two COLUMNS of the head rotation, flattened.
export function buildCond(gaze: Vector3, headPose: HeadPose, out?: Float32Array): Float32Array {
  const cond = out && out.length === 12 ? out : new Float32Array(12)
  cond[0] = gaze[0]; cond[1] = gaze[1]; cond[2] = gaze[2]
  const rotation6d = matrix3.firstTwoColumns(headPose.rotation)
  for (let index = 0; index < 6; index++) cond[3 + index] = rotation6d[index]
  cond[9] = headPose.translation[0]
  cond[10] = headPose.translation[1]
  cond[11] = headPose.translation[2]
  return cond
}

/** A pinhole camera's rays, channel-major (1,6,1,H,W). momentScale 0 zeroes the moment
 *  channels, which encode camera position — appr overfits it to the capture rig. */
export function pluckerFromCamera(origin: Vector3, lookAt: Vector3, fovDegrees: number,
                                  resolution: number, momentScale = 0,
                                  out?: Float32Array): Float32Array {
  const forward = vector3.direction(origin, lookAt)
  let right = vector3.cross(forward, vector3.UP)
  if (vector3.length(right) < 1e-6) right = [1, 0, 0]
  right = vector3.normalize(right)
  const up = vector3.normalize(vector3.cross(right, forward))
  const tanHalfFov = Math.tan((fovDegrees * Math.PI) / 360)

  const pixels = resolution * resolution
  const rays = out && out.length === 6 * pixels ? out : new Float32Array(6 * pixels)
  rays.fill(0)
  for (let row = 0; row < resolution; row++) {
    const ndcY = 1 - (2 * (row + 0.5)) / resolution
    for (let column = 0; column < resolution; column++) {
      const ndcX = (2 * (column + 0.5)) / resolution - 1
      let directionX = forward[0] + right[0] * ndcX * tanHalfFov + up[0] * ndcY * tanHalfFov
      let directionY = forward[1] + right[1] * ndcX * tanHalfFov + up[1] * ndcY * tanHalfFov
      let directionZ = forward[2] + right[2] * ndcX * tanHalfFov + up[2] * ndcY * tanHalfFov
      const magnitude = Math.hypot(directionX, directionY, directionZ) || 1
      directionX /= magnitude; directionY /= magnitude; directionZ /= magnitude
      const pixel = row * resolution + column
      if (momentScale !== 0) {
        rays[0 * pixels + pixel] = (origin[1] * directionZ - origin[2] * directionY) * momentScale
        rays[1 * pixels + pixel] = (origin[2] * directionX - origin[0] * directionZ) * momentScale
        rays[2 * pixels + pixel] = (origin[0] * directionY - origin[1] * directionX) * momentScale
      }
      rays[3 * pixels + pixel] = directionX
      rays[4 * pixels + pixel] = directionY
      rays[5 * pixels + pixel] = directionZ
    }
  }
  return rays
}
